import React, { useState } from 'react';
import { RouteComponentProps } from '@reach/router';
import { Box, Anchor } from 'grommet';
import styled from 'styled-components';
import { customBase } from '../../themes/default.theme';
import useFeatureFlag from '../../util/hooks/useFeatureFlag';
import {
  BillingHistory,
  BillingPayPlans,
  PaymentHistory,
  BillingPayments,
  ChangeDueDate
} from '../../components';
import { CardLayout } from '../../components/cardLayout/CardLayout';
import { TabbedCardLayout } from '../../components/tabbedCardLayout/TabbedCardLayout';
import { simpleGTMDataLayer } from '../../util/GTMHelpers';
import AgencyBillingCard from '../../components/agencyBilling/AgencyBillingCard';
import FeatureFlag from '../../components/featureFlags';
import OutageModal from '../../components/outageModal';

export interface BillingPageProps {
  accountNumber: string;
  editPaymentInfoClick: any;
  makePaymentClick: any;
  agencyBilled: boolean;
  billingLoading: boolean;
  agencyOnly?: boolean;
  allCancelled?: boolean;
  billingCenterOutage: boolean;
  readOnlyAccess: boolean;
  readOnlyFeatureFlag: boolean;
}

const gridPadding = '1.25rem';
const xlargeBreakpoint = `${customBase.global.breakpoints.xlarge.value}px`;

const Container = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-between;
  @media (min-width: ${xlargeBreakpoint}) {
  }
`;
const CenterModal = styled.div`
  align-items: start;
  justify-content: start;
  display: flex;
  flex-direction: column;
`;

const StyledCard = styled(CardLayout)`
  width: 100%;
  &:not(:last-child) {
    margin-bottom: ${gridPadding};
  }
`;

const LastPaymentInfoCard = styled(StyledCard)`
  @media (min-width: ${xlargeBreakpoint}) {
    width: calc(27.5% - ${gridPadding} / 2);
  }
`;

export const PaymentsCard = styled(StyledCard)`
  align-self: flex-start;
  @media (min-width: ${xlargeBreakpoint}) {
    width: calc(55% - ${gridPadding});
  }

  &.PaymentsCardWrap > div:last-child > div {
    position: relative;
    padding: 0;
  }
`;

const NextPaymentInfoCard = styled(StyledCard)`
  @media (min-width: ${xlargeBreakpoint}) {
    width: calc(27.5% - ${gridPadding} / 2);
  }
`;
export const PaymentPlansCard = styled(StyledCard)`
  @media (min-width: ${xlargeBreakpoint}) {
    width: calc(45% - ${gridPadding} / 2);
  }
`;

const CardButtonWrapper = styled.div`
  float: right;
  margin-top: auto;
  margin-bottom: auto;
  margin-right: 5px;

  &:hover {
    cursor: pointer;
  }
  a {
    color: white;
  }
`;

const AgencyBillingContainer = styled(Box)`
  display: flex;
  width: 100%;
  align-items: center;
  margin-top: 45px;
  margin-bottom: 60px;
`;

export const EditPaymentInfoAnchor = styled(Anchor)`
  &:disabled {
    cursor: not-allowed;
  }
`;

export default ({
  editPaymentInfoClick,
  makePaymentClick,
  agencyBilled,
  billingLoading,
  agencyOnly,
  allCancelled,
  readOnlyAccess,
  readOnlyFeatureFlag,
  billingCenterOutage
}: RouteComponentProps<BillingPageProps>) => {
  const [openInvoices, setOpenInvoices] = useState<string[]>([]);
  const [showModal, setShowModal] = useState<boolean>(false);

  const { flagDetails: LastDayToPayFlag } = useFeatureFlag('LastDayToPay');
  const [displayChangeDueDateOutage, setDisplayChangeDueDateOutage] = useState<
    boolean
  >(false);

  const openInvoice = (invoiceNumber: string) => {
    const tmpOpen: string[] = openInvoices.slice(0);
    tmpOpen.push(invoiceNumber);
    setOpenInvoices(tmpOpen);
  };

  const closeInvoice = (invoiceNumber: any) => {
    setOpenInvoices(
      openInvoices.filter(open => {
        return open !== invoiceNumber;
      })
    );
  };

  const tabOnClicks = [
    () => {
      simpleGTMDataLayer({
        event: 'click-billingBillingHistoryTab'
      });
    },
    () => {
      simpleGTMDataLayer({
        event: 'click-billingPaymentHistoryTab'
      });
    }
  ];

  const renderBillingCard = () => {
    if (agencyOnly) {
      return (
        <TabbedCardLayout
          tabTitle={['Agency Billed']}
          open={0}
          tabOnClicks={tabOnClicks}
        >
          <AgencyBillingContainer>
            <AgencyBillingCard />
          </AgencyBillingContainer>
          <Box />
        </TabbedCardLayout>
      );
    }
    if (agencyBilled && !billingLoading && !agencyOnly) {
      return (
        <TabbedCardLayout
          tabTitle={['Billing History', 'Agency Billed', 'Payment History']}
          open={0}
          tabOnClicks={tabOnClicks}
        >
          <BillingHistory
            openInvoices={openInvoices}
            openInvoice={openInvoice}
            closeInvoice={closeInvoice}
          />
          <AgencyBillingContainer>
            <AgencyBillingCard />
          </AgencyBillingContainer>
          <PaymentHistory />
        </TabbedCardLayout>
      );
    }

    if (!agencyBilled && !billingLoading) {
      return (
        <TabbedCardLayout
          tabTitle={['Invoices', 'Payment History']}
          open={0}
          tabOnClicks={tabOnClicks}
        >
          <BillingHistory
            openInvoices={openInvoices}
            openInvoice={openInvoice}
            closeInvoice={closeInvoice}
          />
          <PaymentHistory />
        </TabbedCardLayout>
      );
    }
  };

  return (
    <CenterModal>
      {showModal && <ChangeDueDate setShowModal={setShowModal} />}
      <Container>
        <PaymentsCard
          className={LastDayToPayFlag?.enabled ? 'PaymentsCardWrap' : ''}
          title="Payments"
          buttonComponent={
            <FeatureFlag flagName="ChangeDueDate">
              <CardButtonWrapper>
                <Anchor
                  test-attr="billing-page-change-payment-anchor"
                  onClick={() => {
                    if (billingCenterOutage) {
                      setDisplayChangeDueDateOutage(true);
                    } else {
                      simpleGTMDataLayer({
                        event: 'click-billingChangeDueDate',
                        event_action: 'Button Click',
                        event_category: window.location.pathname,
                        event_category_snowplow: `${window.location.pathname}/Snowplow`,
                        event_label: 'Change Due Date'
                      });
                      setShowModal(true);
                    }
                  }}
                  label="CHANGE PAYMENT DUE DATE"
                />
              </CardButtonWrapper>
            </FeatureFlag>
          }
        >
          <BillingPayments
            lastDayToPayFlag={LastDayToPayFlag?.enabled}
            makePaymentClick={makePaymentClick}
            allCancelled={allCancelled}
            readOnlyAccess={readOnlyAccess}
            readOnlyFeatureFlag={readOnlyFeatureFlag}
          />
        </PaymentsCard>
        <PaymentPlansCard
          bodyOverflow="auto"
          bodyHeight="165px"
          title="Payment Plans"
          additionalBottomGap="30px"
          buttonComponent={
            <CardButtonWrapper>
              <EditPaymentInfoAnchor
                onClick={() => {
                  editPaymentInfoClick();
                }}
                label="EDIT PAYMENT INFO"
                disabled={readOnlyAccess && readOnlyFeatureFlag}
              />
            </CardButtonWrapper>
          }
        >
          <BillingPayPlans />
        </PaymentPlansCard>
        {renderBillingCard()}
      </Container>
      {displayChangeDueDateOutage && (
        <OutageModal
          title="Change Payment Due Date is Unavailable"
          onClose={() => setDisplayChangeDueDateOutage(false)}
        >
          Unfortunately, due to server maintenance, changing your payment due
          date is currently unavailable. Please check back in 24 to 48 hours.
        </OutageModal>
      )}
    </CenterModal>
  );
};
